import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComparacionDuplicados, DuplicadoCandidato, ElementoCatalogo, ErrorDuplicados, EstadoFusion, FusionPendiente, FusionRequest, InformeFusion,
  ResultadoCuentaWeb, ResultadoEliminacion, ResultadoFusion, aplicarFusion, buscarFusionesPendientes, compararClientes, eliminarDuplicado,
  interpretarErrorDuplicados, leerCatalogosDuplicados, leerInformeFusion, resolverCuentaWeb, simularFusion,
} from '../../../api/clientesDuplicadosApi';
import {
  CambioElemento, EDICION_VACIA, EdicionFusion, aplicarReglas, LineaInforme, TipoListaEditable, TipoPreferencia, claveCampo, claveElemento,
  construirEdicion, intercambiarComparacion, lineaCuentaWeb, lineasDeFusion, lineasEliminacion, lineasPendientes,
  formatearValor, pendienteDeLaPareja, resultadoDesdeInforme,
} from './reglas';

/** Los 6 pasos de §4: 1 Selección · 2 Verificación · 3 Superviviente · 4 Fusión · 5 Tratamiento · 6 Cierre. */
export type Paso = 1 | 2 | 3 | 4 | 5 | 6;

export type EstadoEjecucion = 'inactiva' | 'en_curso' | 'completada' | 'error';

/** Catálogos de los desplegables del paso 4, por nombre de catálogo (`CampoEnfrentado.catalogo`). */
export type Catalogos = Record<string, ElementoCatalogo[]>;

/**
 * Estado del tratamiento (paso 5). Se conserva entre reintentos: `aplicar` crea la fusión y a
 * partir de ahí `cuenta-web` y `eliminar` son reentrantes (D-GC-38, R-GC-10), así que si falla
 * el paso 2 o el 3 se reanuda desde ahí, sin volver a aplicar.
 */
export interface EjecucionFusion {
  estado: EstadoEjecucion;
  /** Fases completadas: 0 ninguna · 1 aplicada · 2 cuenta web · 3 eliminado · 4 informe. */
  fase: number;
  idFusion: number | null;
  aplicado: ResultadoFusion | null;
  cuentaWeb: ResultadoCuentaWeb | null;
  eliminacion: ResultadoEliminacion | null;
  informe: InformeFusion | null;
  error: ErrorDuplicados | null;
  lineas: LineaInforme[];
}

const EJECUCION_INICIAL: EjecucionFusion = {
  estado: 'inactiva', fase: 0, idFusion: null, aplicado: null, cuentaWeb: null, eliminacion: null,
  informe: null, error: null, lineas: [],
};

/**
 * Orquestación del asistente de duplicados (GESCL-T07 §4). Aquí vive el estado; los
 * componentes solo pintan. Convención fija de toda la pantalla: izquierda = se conserva,
 * derecha = se elimina (D-GC-41/42). Desde el script 030 la izquierda además se EDITA: lo
 * tocado vive en `edicion` y «restaurar» lo saca de ahí.
 */
export const useAsistenteDuplicados = () => {
  const [candidatoA, setCandidatoA] = useState<DuplicadoCandidato | null>(null);
  const [candidatoB, setCandidatoB] = useState<DuplicadoCandidato | null>(null);

  const [comparacionServidor, setComparacionServidor] = useState<ComparacionDuplicados | null>(null);
  const [cargandoComparacion, setCargandoComparacion] = useState(false);
  const [errorComparacion, setErrorComparacion] = useState<ErrorDuplicados | null>(null);
  /** Solo en el caso LIBRE: el operador ha dado la vuelta a la sugerencia del servidor. */
  const [intercambiada, setIntercambiada] = useState(false);

  const [paso, setPaso] = useState<Paso>(1);
  const [pasoMaximo, setPasoMaximo] = useState<Paso>(1);

  /** Lo que el operador ha tocado en la columna izquierda (paso 4). Solo lo tocado. */
  const [edicion, setEdicion] = useState<EdicionFusion>(EDICION_VACIA);

  /** Catálogos de los desplegables. Se cargan una vez; si fallan, las celdas de catálogo se quedan de lectura. */
  const [catalogos, setCatalogos] = useState<Catalogos>({});
  const [errorCatalogos, setErrorCatalogos] = useState<ErrorDuplicados | null>(null);

  const [simulacion, setSimulacion] = useState<ResultadoFusion | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [errorSimulacion, setErrorSimulacion] = useState<ErrorDuplicados | null>(null);

  const [motivo, setMotivo] = useState('');
  const [ejecucion, setEjecucion] = useState<EjecucionFusion>(EJECUCION_INICIAL);

  const idA = candidatoA?.idCliente ?? null;
  const idB = candidatoB?.idCliente ?? null;

  /** Vista que se pinta: la del servidor o, en LIBRE con intercambio, la recalculada. */
  const comparacion = useMemo(
    () => (comparacionServidor && intercambiada ? intercambiarComparacion(comparacionServidor) : comparacionServidor),
    [comparacionServidor, intercambiada],
  );
  const fichaConserva = comparacion?.fichas.find((f) => f.lado === 1) ?? null;
  const fichaElimina = comparacion?.fichas.find((f) => f.lado === 2) ?? null;

  const irreversibleIniciado = ejecucion.aplicado !== null;
  const puedeIntercambiar = Boolean(comparacion && comparacion.regla.resultado === 'LIBRE' && !irreversibleIniciado && paso <= 4);

  // ── Catálogos ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const lista = await leerCatalogosDuplicados();
        if (cancelado) return;
        const porCatalogo: Catalogos = {};
        for (const e of lista) (porCatalogo[e.catalogo] ??= []).push(e);
        setCatalogos(porCatalogo);
      } catch (e) {
        if (!cancelado) setErrorCatalogos(interpretarErrorDuplicados(e));
      }
    })();
    return () => { cancelado = true; };
  }, []);

  // ── Comparar ──────────────────────────────────────────────────
  const marcaComparacion = useRef(0);
  const comparar = useCallback(async (c1: number, c2: number) => {
    const marca = ++marcaComparacion.current;
    setCargandoComparacion(true);
    setErrorComparacion(null);
    try {
      const res = await compararClientes(c1, c2);
      if (marca !== marcaComparacion.current) return null;
      setComparacionServidor(res);
      return res;
    } catch (e) {
      if (marca !== marcaComparacion.current) return null;
      setComparacionServidor(null);
      setErrorComparacion(interpretarErrorDuplicados(e));
      return null;
    } finally {
      if (marca === marcaComparacion.current) setCargandoComparacion(false);
    }
  }, []);

  /** Al elegir la segunda ficha, la pantalla pinta las dos columnas (§4.1) y pasa al paso 2. */
  useEffect(() => {
    if (idA === null || idB === null) {
      setComparacionServidor(null);
      setIntercambiada(false);
      setPaso(1);
      setPasoMaximo(1);
      return;
    }
    let cancelado = false;
    (async () => {
      const res = await comparar(idA, idB);
      if (cancelado || !res) return;
      setIntercambiada(false);
      setEdicion(EDICION_VACIA);
      setSimulacion(null);
      setPaso(2);
      setPasoMaximo(2);
    })();
    return () => { cancelado = true; };
  }, [idA, idB, comparar]);

  /**
   * Cualquier cambio en lo que se va a fusionar deja obsoleta la simulación y, si el tratamiento
   * había fallado ANTES de aplicar (barrera, validación…), también ese error: se vuelve a empezar
   * el paso 5 limpio. Lo ya aplicado nunca se descarta.
   */
  const invalidarSimulacion = useCallback(() => {
    setSimulacion(null);
    setErrorSimulacion(null);
    setEjecucion((e) => (e.aplicado ? e : EJECUCION_INICIAL));
  }, []);

  /**
   * «Recargar los dos» (§4.3, D-GC-41): vuelve a leer ambas fichas sin perder el trabajo hecho.
   * Es la herramienta con la que se levanta un bloqueo: si ha desaparecido, la regla se
   * reevalúa y el proceso sigue. Lo editado se conserva (va por `tabla.campo` y por id de
   * elemento); el intercambio solo si el caso sigue siendo LIBRE.
   */
  const recargar = useCallback(async () => {
    if (idA === null || idB === null) return null;
    const anterior = comparacionServidor;
    const res = await comparar(idA, idB);
    if (!res) return null;
    invalidarSimulacion();
    if (res.regla.resultado !== 'LIBRE') setIntercambiada(false);
    // Si la regla ha cambiado, lo decidido a partir del paso 3 deja de valer: se vuelve al 3.
    if (anterior && anterior.regla.resultado !== res.regla.resultado && pasoMaximo > 3) {
      setPaso(3);
      setPasoMaximo(3);
    }
    return res;
  }, [idA, idB, comparar, comparacionServidor, pasoMaximo, invalidarSimulacion]);

  const intercambiar = useCallback(() => {
    if (!puedeIntercambiar) return;
    setIntercambiada((v) => !v);
    // Los valores cambian de lado: lo editado ya no significa lo mismo.
    setEdicion(EDICION_VACIA);
    invalidarSimulacion();
  }, [puedeIntercambiar, invalidarSimulacion]);

  // ── Navegación entre pasos ────────────────────────────────────
  const puedeIrAPaso = useCallback((n: Paso) => {
    if (irreversibleIniciado) return n === paso || (ejecucion.estado === 'completada' && n === 6);
    if (n > pasoMaximo) return false;
    if (n >= 3 && !comparacion) return false;
    // Con bloqueo (D-GC-41) no se pasa del paso 3: primero hay que resolverlo fuera y recargar.
    if (n > 3 && comparacion?.regla.bloqueo) return false;
    return true;
  }, [irreversibleIniciado, paso, pasoMaximo, comparacion, ejecucion.estado]);

  const irAPaso = useCallback((n: Paso) => {
    if (!puedeIrAPaso(n)) return;
    if (n < 5) invalidarSimulacion();
    setPaso(n);
  }, [puedeIrAPaso, invalidarSimulacion]);

  const avanzar = useCallback(() => {
    const siguiente = Math.min(paso + 1, 6) as Paso;
    setPasoMaximo((m) => (siguiente > m ? siguiente : m));
    setPaso(siguiente);
  }, [paso]);

  // ── Edición del paso 4 (script 030) ───────────────────────────
  const tocar = useCallback((cambio: (e: EdicionFusion) => EdicionFusion) => {
    setEdicion(cambio);
    invalidarSimulacion();
  }, [invalidarSimulacion]);

  /** Escribe en una celda. `null` = vaciar. */
  const editarValor = useCallback((c: { tabla: string; campo: string }, valor: string | null) => {
    tocar((e) => ({ ...e, valores: { ...e.valores, [claveCampo(c)]: valor } }));
  }, [tocar]);

  /** Vuelve la celda a la premarca. */
  const restaurarValor = useCallback((c: { tabla: string; campo: string }) => {
    tocar((e) => {
      const { [claveCampo(c)]: _fuera, ...resto } = e.valores;
      return { ...e, valores: resto };
    });
  }, [tocar]);

  /** Cambia un email o teléfono de la lista fusionada (eliminar, número, tipo). */
  const cambiarElemento = useCallback((tipo: TipoListaEditable, idElemento: number, cambio: CambioElemento) => {
    tocar((e) => {
      const k = claveElemento(tipo, idElemento);
      return { ...e, elementos: { ...e.elementos, [k]: { ...(e.elementos[k] ?? {}), ...cambio } } };
    });
  }, [tocar]);

  /** Deshace todo lo hecho con ese email o teléfono. */
  const restaurarElemento = useCallback((tipo: TipoListaEditable, idElemento: number) => {
    tocar((e) => {
      const { [claveElemento(tipo, idElemento)]: _fuera, ...resto } = e.elementos;
      return { ...e, elementos: resto };
    });
  }, [tocar]);

  /** Marca el principal de la lista (null = ninguno). */
  const marcarPrincipal = useCallback((tipo: TipoListaEditable, idElemento: number | null) => {
    tocar((e) => ({ ...e, principal: { ...e.principal, [tipo]: idElemento } }));
  }, [tocar]);

  /** Fija el conjunto de preferencias (`undefined` = volver a la premarca). */
  const fijarPreferencias = useCallback((tipo: TipoPreferencia, ids: number[] | undefined) => {
    tocar((e) => {
      if (ids === undefined) {
        const { [tipo]: _fuera, ...resto } = e.preferencias;
        return { ...e, preferencias: resto };
      }
      return { ...e, preferencias: { ...e.preferencias, [tipo]: ids } };
    });
  }, [tocar]);

  const restaurarTodo = useCallback(() => { tocar(() => EDICION_VACIA); }, [tocar]);

  /** Pasa a la izquierda lo que las reglas de dato ganador toman del duplicado, en lo no tocado. */
  const aplicarReglasDatoGanador = useCallback(() => {
    if (!comparacion) return;
    tocar((e) => aplicarReglas(comparacion, e));
  }, [tocar, comparacion]);

  // ── Simulación (paso 5) ───────────────────────────────────────
  const peticion = useCallback((): FusionRequest | null => {
    if (!comparacion || !fichaConserva || !fichaElimina) return null;
    const cuerpo = construirEdicion(comparacion, edicion);
    return {
      idClienteConservar: fichaConserva.idCliente,
      idClienteEliminar: fichaElimina.idCliente,
      motivo: motivo.trim() || null,
      elecciones: cuerpo.elecciones,
      valores: cuerpo.valores,
      elementos: cuerpo.elementos,
    };
  }, [comparacion, fichaConserva, fichaElimina, motivo, edicion]);

  /**
   * ¿Se llegó a aplicar esta fusión aunque no lo sepamos? (S-03/S-19, GESCL-T08). Se pregunta al
   * servidor por una fusión APLICADA sin terminar de esta pareja en este sentido y, si la hay, se
   * reconstruye su resultado para reanudarla desde la cuenta web. Null = no la hay (o no se pudo
   * saber): entonces sí se puede tratar como no aplicada.
   */
  const reconciliar = useCallback(async (req: FusionRequest): Promise<{ resultado: ResultadoFusion; pendiente: FusionPendiente } | null> => {
    try {
      const pendientes = await buscarFusionesPendientes(req.idClienteConservar, req.idClienteEliminar);
      const pendiente = pendienteDeLaPareja(pendientes, req.idClienteConservar, req.idClienteEliminar);
      if (!pendiente) return null;
      return { resultado: resultadoDesdeInforme(await leerInformeFusion(pendiente.idClienteFusion)), pendiente };
    } catch {
      return null;
    }
  }, []);

  /** La fusión recuperada se ofrece para reanudar, sin seguir sola: el operador decide. */
  const ofrecerReanudar = useCallback((resultado: ResultadoFusion, pendiente: FusionPendiente, mensaje: string) => {
    setEjecucion({
      ...EJECUCION_INICIAL,
      estado: 'error',
      fase: 1,
      idFusion: resultado.idClienteFusion,
      aplicado: resultado,
      lineas: lineasDeFusion(resultado),
      error: {
        titulo: 'Fusión sin terminar',
        mensaje: `${mensaje} La aplicó ${pendiente.usuarioAlta ?? 'otro operador'}` +
          `${pendiente.fechaAplicacion ? ` el ${formatearValor(pendiente.fechaAplicacion)}` : ''}. ` +
          'Reanuda el tratamiento para resolver la cuenta web y eliminar el duplicado con lo que se aplicó entonces.',
      },
    });
  }, []);

  const simular = useCallback(async () => {
    const req = peticion();
    if (!req) return;
    setSimulando(true);
    setErrorSimulacion(null);
    try {
      setSimulacion(await simularFusion({ ...req, motivo: null }));
    } catch (e) {
      setSimulacion(null);
      const error = interpretarErrorDuplicados(e);
      // Una fusión de esta pareja se aplicó y quedó sin terminar: no se simula otra, se ofrece
      // reanudar la que hay (el botón «Reanudar el tratamiento» sale con `aplicado` y estado error).
      const recuperada = error.idFusionPendiente != null ? await reconciliar(req) : null;
      if (recuperada) {
        ofrecerReanudar(recuperada.resultado, recuperada.pendiente,
          `La fusión nº ${recuperada.resultado.idClienteFusion} de estas dos fichas ya se aplicó y quedó a medias.`);
      } else {
        setErrorSimulacion(error);
      }
    } finally {
      setSimulando(false);
    }
  }, [peticion, reconciliar, ofrecerReanudar]);

  /** Al entrar en el paso 5 se simula de verdad antes de tocar nada (D-GC-37). */
  useEffect(() => {
    if (paso === 5 && !simulacion && !simulando && !errorSimulacion && ejecucion.aplicado === null && ejecucion.estado !== 'en_curso') {
      simular();
    }
  }, [paso, simulacion, simulando, errorSimulacion, ejecucion.aplicado, ejecucion.estado, simular]);

  const simulacionLimpia = Boolean(simulacion && simulacion.idEstado === EstadoFusion.Simulada && simulacion.pendientes.length === 0);

  // ── Tratamiento (paso 5 → 6): aplicar → cuenta web → eliminar → informe (§4.8) ──
  const ejecutar = useCallback(async () => {
    const req = peticion();
    if (!req) return;
    let acc: EjecucionFusion = { ...ejecucion, estado: 'en_curso', error: null };
    const publicar = () => setEjecucion({ ...acc });
    publicar();
    try {
      if (!acc.aplicado) {
        let aplicado: ResultadoFusion;
        try {
          // D-GC-87: aplicar lleva la simulación que se acaba de hacer; el servidor la comprueba.
          aplicado = await aplicarFusion({ ...req, idSimulacion: simulacion?.idClienteFusion ?? null });
        } catch (e) {
          const error = interpretarErrorDuplicados(e);
          if (error.volverASimular) {
            // La simulación caducó o no casa con lo que hay en pantalla: se simula de nuevo sola
            // (efecto de más arriba) y el operador repite. No se ha tocado nada.
            setSimulacion(null);
            setErrorSimulacion(null);
            setEjecucion(EJECUCION_INICIAL);
            return;
          }
          // Sin respuesta, 5xx o «fusión sin terminar»: puede que SÍ se aplicara (S-03). Antes de
          // dar nada por perdido —y de dejar que se reintente— se pregunta al servidor.
          const dudoso = error.sinRespuesta === true || (error.status ?? 0) >= 500 || error.idFusionPendiente != null;
          const recuperado = dudoso ? await reconciliar(req) : null;
          if (!recuperado) throw e;
          // Solo se sigue sola si es la fusión que salió de MI simulación (N-03). Si la aplicó
          // otro operador —con sus elecciones— entre mi simulación y mi aplicar, se para y se ofrece.
          if (simulacion === null || recuperado.pendiente.idFusionSimulada !== simulacion.idClienteFusion) {
            ofrecerReanudar(recuperado.resultado, recuperado.pendiente,
              `Estas dos fichas ya tenían una fusión aplicada sin terminar (nº ${recuperado.resultado.idClienteFusion}), que no es la tuya.`);
            return;
          }
          aplicado = recuperado.resultado;
        }
        if (aplicado.idEstado !== EstadoFusion.Aplicada) {
          // NO_LIMPIA o ERROR: no se ha aplicado nada y se puede volver a intentar desde cero.
          acc = {
            ...acc,
            estado: 'error',
            lineas: lineasDeFusion(aplicado),
            error: {
              titulo: aplicado.idEstado === EstadoFusion.NoLimpia ? 'Quedan referencias al cliente' : 'La fusión ha fallado',
              mensaje: aplicado.idEstado === EstadoFusion.NoLimpia
                ? 'No se ha aplicado nada: quedaban referencias al duplicado después del traspaso. Vuelve a simular.'
                : `No se ha aplicado nada. Indica a soporte el número de fusión ${aplicado.idClienteFusion}.`,
            },
          };
          publicar();
          return;
        }
        acc = { ...acc, aplicado, idFusion: aplicado.idClienteFusion, fase: 1, lineas: lineasDeFusion(aplicado) };
        publicar();
      }
      const idFusion = acc.idFusion!;
      /*
       * «Ya completada» (409 con yaCompletada) no es un fallo: la respuesta de un paso anterior se
       * perdió y el duplicado ya se había eliminado. Se salta al informe en vez de invitar a
       * repetir (S-13).
       */
      let yaCompletada = false;
      const siYaCompletada = (e: unknown) => {
        if (!interpretarErrorDuplicados(e).yaCompletada) throw e;
        yaCompletada = true;
        acc = {
          ...acc, fase: 3,
          lineas: [...acc.lineas, { clave: 'ya-completada', estado: 'ok', texto: 'El duplicado ya se había eliminado: la fusión estaba completada.' }],
        };
        publicar();
      };
      if (!acc.cuentaWeb) {
        try {
          const cuentaWeb = await resolverCuentaWeb(idFusion);
          acc = { ...acc, cuentaWeb, fase: 2, lineas: [...acc.lineas, lineaCuentaWeb(cuentaWeb)] };
          publicar();
        } catch (e) {
          siYaCompletada(e);
        }
      }
      if (!acc.eliminacion && !yaCompletada) {
        try {
          const eliminacion = await eliminarDuplicado(idFusion);
          acc = {
            ...acc, eliminacion, fase: 3,
            lineas: [...acc.lineas, ...lineasEliminacion(eliminacion, acc.aplicado!.codigoEliminado)],
          };
          publicar();
        } catch (e) {
          siYaCompletada(e);
        }
      }
      const informe = await leerInformeFusion(idFusion);
      acc = { ...acc, informe, fase: 4, estado: 'completada' };
      publicar();
      // El salto al paso 6 lo da el operador al cerrar el modal con el informe (PasoTratamiento).
      setPasoMaximo(6);
    } catch (e) {
      const error = interpretarErrorDuplicados(e);
      const lineas = error.pendientes ? [...acc.lineas, lineasPendientes(error.pendientes)] : acc.lineas;
      acc = { ...acc, estado: 'error', error, lineas };
      publicar();
    }
  }, [peticion, ejecucion, simulacion, reconciliar, ofrecerReanudar]);

  /** Formulario limpio, conservando el enlace al informe recién hecho (§4.6): lo guarda la página. */
  const reiniciar = useCallback(() => {
    marcaComparacion.current += 1;
    setCandidatoA(null);
    setCandidatoB(null);
    setComparacionServidor(null);
    setErrorComparacion(null);
    setIntercambiada(false);
    setEdicion(EDICION_VACIA);
    setSimulacion(null);
    setErrorSimulacion(null);
    setMotivo('');
    setEjecucion(EJECUCION_INICIAL);
    setPaso(1);
    setPasoMaximo(1);
  }, []);

  return {
    candidatoA, candidatoB, setCandidatoA, setCandidatoB,
    comparacion, cargandoComparacion, errorComparacion,
    fichaConserva, fichaElimina,
    intercambiada, puedeIntercambiar, intercambiar, recargar,
    paso, pasoMaximo, puedeIrAPaso, irAPaso, avanzar,
    edicion, editarValor, restaurarValor, cambiarElemento, restaurarElemento, marcarPrincipal, fijarPreferencias, restaurarTodo,
    aplicarReglasDatoGanador,
    catalogos, errorCatalogos,
    simulacion, simulando, errorSimulacion, simular, simulacionLimpia,
    motivo, setMotivo,
    ejecucion, ejecutar, irreversibleIniciado,
    reiniciar,
  };
};

export type AsistenteDuplicados = ReturnType<typeof useAsistenteDuplicados>;
